/**
 * The 32 NHL clubs: the table every `/nhl/*` page joins its markets against.
 *
 * `nhlTeamDirectory.json` is generated — `node scripts/generate-nhl-team-directory.mjs`
 * writes it from Kalshi's own structured targets — so the table and the ids the
 * markets carry can't drift. Edit the generator, not the file.
 *
 * Identity only: a club's conference, division, names, logo, palette and page.
 * Deliberately no form — no record, no points, no standing. Those come from the
 * team-hub live-data lane (`_lib/nhlHub.ts`), which is refreshed every sweep;
 * anything copied into a committed file would be quoted as current long after it
 * stopped being true.
 *
 * Like basketball's, hockey's standings rows arrive carrying their own
 * `structured_target_id`, so the market join needs no lookup here. What this
 * table supplies is what the feed does *not*: the page a club lives at, its logo,
 * its palette key, and a stable league-wide ordering.
 */
import nhlTeamDirectoryJson from './nhlTeamDirectory.json';

/** Which conference a club plays in. */
export type NhlConference = 'East' | 'West';

/** The two divisions each conference is split into. */
export type NhlDivisionName = 'Atlantic' | 'Metropolitan' | 'Central' | 'Pacific';

/** One club's row in the committed directory: who it is, not how it is doing. */
export interface NhlTeamIdentity {
    /** Kalshi structured-target id — the join key every market family carries. */
    id: string;
    /** City/market half, e.g. "Boston" — how Kalshi titles its markets. */
    market: string;
    /** Nickname alone, e.g. "Maple Leafs". Unprefixed on every hockey target. */
    teamName: string;
    /** Full name, e.g. "Toronto Maple Leafs". Always `market` + `teamName` today. */
    name: string;
    conference: NhlConference;
    division: NhlDivisionName;
    /**
     * The `/teams/hockey-team/[name]` segment this club is served at today —
     * `bos-bruins`, `tor-maple-leafs`, `vgk-golden-knights`.
     *
     * Slugified from the structured target's own `name` ("BOS Bruins"), the rule
     * `sitemapper.js` generates those URLs with. It is also the key the club's
     * copy is filed under in `HOCKEY_FAQ_CONFIGS` — hockey has one spelling where
     * basketball has two, and the test pins them together.
     */
    legacySlug: string;
    /** The `/nhl/teams/[team]` segment: city and mascot, e.g. `boston-bruins`. */
    pageSlug: string;
    logoUrl: string;
    /** The `colorsPalettes.ts` key for this club, e.g. "NHL-BOS". */
    colorPalette: string;
}

export interface NhlTeamDirectoryEntry extends NhlTeamIdentity {
    /**
     * Kalshi's own code, e.g. "BOS" — the market ticker's suffix.
     *
     * Two or three letters, not always three: Los Angeles is `LA`, New Jersey
     * `NJ`, San Jose `SJ`, Tampa Bay `TB`. Anything that pads or slices to three
     * characters breaks exactly those four.
     */
    abbreviation: string;
    /** `/nhl/teams/boston-bruins` — where every link to a club should point. */
    path: string;
}

const teams = nhlTeamDirectoryJson.teams as Record<string, NhlTeamIdentity>;

/** Every club, alphabetically by full name. */
export const NHL_TEAM_DIRECTORY: NhlTeamDirectoryEntry[] = Object.entries(teams)
    .map(([abbreviation, team]) => ({ ...team, abbreviation, path: `/nhl/teams/${team.pageSlug}` }))
    .sort((first, second) => first.name.localeCompare(second.name));

const NHL_TEAM_BY_TARGET_ID = new Map(NHL_TEAM_DIRECTORY.map((team) => [team.id, team]));
const NHL_TEAM_BY_ABBREVIATION = new Map(NHL_TEAM_DIRECTORY.map((team) => [team.abbreviation, team]));
const NHL_TEAM_BY_PAGE_SLUG = new Map(NHL_TEAM_DIRECTORY.map((team) => [team.pageSlug, team]));
const NHL_TEAM_BY_LEGACY_SLUG = new Map(NHL_TEAM_DIRECTORY.map((team) => [team.legacySlug, team]));

/**
 * The club a market resolves for, from the structured-target id in its
 * `custom_strike.hockey_team`.
 *
 * The join every NHL page should use. A ticker suffix is a naming convention
 * rather than a contract; the structured-target id is the field the exchange
 * publishes the relationship in.
 */
export const findNhlTeamByTargetId = (targetId: string | undefined): NhlTeamDirectoryEntry | undefined =>
    targetId ? NHL_TEAM_BY_TARGET_ID.get(targetId) : undefined;

export const findNhlTeamByAbbreviation = (abbreviation: string | undefined): NhlTeamDirectoryEntry | undefined =>
    abbreviation ? NHL_TEAM_BY_ABBREVIATION.get(abbreviation.trim().toUpperCase()) : undefined;

/**
 * The club a `/nhl/teams/[team]` request names, e.g. `boston-bruins`.
 *
 * An unknown slug is `undefined` here — and a 404 at the route — with no API
 * call, which is what the name-search lookup behind `/teams/[type]/[name]`
 * cannot promise: that one returns its best-scoring candidate.
 */
export const findNhlTeamByPageSlug = (slug: string | undefined): NhlTeamDirectoryEntry | undefined =>
    slug ? NHL_TEAM_BY_PAGE_SLUG.get(slug.trim().toLowerCase()) : undefined;

/**
 * The club a legacy `/teams/hockey-team/[name]` request names, e.g. `bos-bruins`.
 *
 * Only the 32 NHL spellings resolve. The same route serves about 140 other
 * hockey sides — AHL, KHL, college, national — and none of them may be
 * redirected onto an NHL page.
 */
export const findNhlTeamByLegacySlug = (slug: string | undefined): NhlTeamDirectoryEntry | undefined =>
    slug ? NHL_TEAM_BY_LEGACY_SLUG.get(slug.trim().toLowerCase()) : undefined;

/**
 * How many clubs' hubs to try before giving up on the league-wide read.
 *
 * The lane is queried per club but answers with the whole league's standings,
 * so one club whose sweep slot failed would otherwise take every standings page
 * down. Three costs nothing on the happy path, because `fetchNhlHub` stops at
 * the first that answers; read off the front of the name-sorted directory so
 * they are the same three on every deployment.
 */
export const NHL_UPSTREAM_ANCHOR_COUNT = 3;

/** Kalshi's own NHL category hub. Linked from the existing NHL team pages' copy. */
export const NHL_HUB_PATH = '/category/sports/hockey/nhl';

/** Both conferences, East first — the order every page lists them in. */
export const NHL_CONFERENCES: readonly NhlConference[] = ['East', 'West'] as const;

/** The full name of a conference, for prose and headings. */
export const NHL_CONFERENCE_NAMES: Record<NhlConference, string> = {
    East: 'Eastern Conference',
    West: 'Western Conference',
};

/**
 * The two divisions of each conference, in the order the league lists them.
 *
 * Written down rather than discovered from the directory so sections keep a
 * stable order across renders.
 */
export const NHL_DIVISIONS: Record<NhlConference, readonly NhlDivisionName[]> = {
    East: ['Atlantic', 'Metropolitan'],
    West: ['Central', 'Pacific'],
};

/** The clubs of one conference, alphabetically. */
export const nhlTeamsInConference = (conference: NhlConference): NhlTeamDirectoryEntry[] =>
    NHL_TEAM_DIRECTORY.filter((team) => team.conference === conference);

/** How many clubs each conference sends to the playoffs. */
export const NHL_PLAYOFF_SPOTS_PER_CONFERENCE = 8;

/**
 * How many of those eight are division places: the top three of each division.
 *
 * The other two per conference are wild cards — the best two remaining records
 * in the conference regardless of division — which is what `/nhl/wild-card-standings`
 * exists to show.
 */
export const NHL_DIVISION_PLAYOFF_SPOTS = 3;

/** Wild-card places per conference. */
export const NHL_WILD_CARDS_PER_CONFERENCE = 2;

/**
 * Games in an NHL regular season, by the season's starting year.
 *
 * 84 from 2026-27, 82 before — measured on the hub schedule, which carries 82
 * `REG` rows per club for `?season=2025` and 84 for 2026. A function rather than
 * a constant because the lane serves both seasons: it is the denominator for
 * points-pace arithmetic, and one number would mis-project whichever season it
 * was not written for.
 */
export const nhlRegularSeasonGames = (season: number): number => (season >= 2026 ? 84 : 82);
